import React, { useMemo, useState } from 'react';
import { Loader, RefreshCw, Building2, GripVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ClientCard from './ClientCard';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface PanelClient {
  uid: string;
  name: string;
  display_name?: string;
  database?: {
    host: string;
    user: string;
    database: string;
    password: string;
  };
  logo?: string;
  logoHeight?: number;
  welcomeVideo?: string;
  greetingVideo?: string;
  userCount?: number;
  tableCount?: number;
  tableError?: boolean;
  tableErrorMessage?: string;
  lastTomaDate?: string | null;
  active?: boolean;
  client_type?: string;
  catalogadoPrompt?: string;
  enable_tags?: boolean;
  enable_sku?: boolean;
  last_sync_at?: string;
}

interface Props {
  title: string;
  clients: PanelClient[];
  loading: boolean;
  onRefresh: () => void;
  onClientPatch?: (uid: string, patch: Record<string, any>) => void;
  onVideoUpload: (file: File, clientId: string, videoType: 'welcomeVideo' | 'greetingVideo') => void;
  uploadingVideo: string | null;
  uploadingGreetingVideo: string | null;
  showSyncButton?: boolean;
  onSync?: () => void;
  syncing?: boolean;
  syncLabel?: string;
  lastSyncAt?: string | null;
  lastSyncHasTime?: boolean;
  syncProgress?: { total: number; completed: number; failed: number; active: boolean };
  onReorder?: (uidsInNewOrder: string[]) => void;
  autoOpenEditUid?: string | null;
}

const formatLastSync = (iso?: string | null, hasTime: boolean = true): string => {
  if (!iso) return 'nunca';
  try {
    const opts: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };
    if (hasTime) {
      opts.hour = '2-digit';
      opts.minute = '2-digit';
      opts.hour12 = false;
    }
    return new Date(iso).toLocaleString('es-AR', opts);
  } catch {
    return iso;
  }
};

const isActive = (a?: boolean) => a !== false;

// Wrapper sortable para cada card. Drag handle a la izquierda.
const SortableClientCard: React.FC<{ id: string; canDrag: boolean; children: React.ReactNode }> = ({ id, canDrag, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !canDrag });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : 'auto' as any,
  };
  if (!canDrag) {
    return <div ref={setNodeRef} style={style}>{children}</div>;
  }
  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch">
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="flex items-center px-1 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
        title="Arrastrar para reordenar"
        aria-label="Drag handle"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

const ClientsPanel: React.FC<Props> = ({
  title,
  clients,
  loading,
  onRefresh,
  onClientPatch,
  onVideoUpload,
  uploadingVideo,
  uploadingGreetingVideo,
  showSyncButton = false,
  onSync,
  syncing = false,
  syncLabel,
  lastSyncAt,
  lastSyncHasTime = true,
  autoOpenEditUid,
  syncProgress,
  onReorder,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'active' | 'inactive'>('active');

  // Sensores: PointerSensor con umbral de 6px para no hijack clicks accidentales.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    if (!onReorder) return;
    // Reordenar solo dentro del tab activo (visible list).
    const oldIndex = visible.findIndex(c => c.uid === active.id);
    const newIndex = visible.findIndex(c => c.uid === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(visible, oldIndex, newIndex);
    onReorder(reordered.map(c => c.uid));
  };

  const { activeClients, inactiveClients } = useMemo(() => {
    const a: PanelClient[] = [];
    const i: PanelClient[] = [];
    for (const c of clients) {
      if (isActive(c.active)) a.push(c);
      else i.push(c);
    }
    return { activeClients: a, inactiveClients: i };
  }, [clients]);

  const visible = tab === 'active' ? activeClients : inactiveClients;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
      {/* HEADER: título prominente, línea propia */}
      <div className="px-5 pt-4 pb-3 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">
          {title}
        </h2>
      </div>

      {/* TOOLBAR: tabs estilo subrayado clásico (mas reconocible) + meta + sync */}
      <div className="border-b border-gray-200 px-5 flex items-center justify-between gap-3 flex-wrap">
        <nav className="flex gap-1 -mb-px">
          <button
            onClick={() => setTab('active')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === 'active'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            {t('admin.panel.activeTab', 'Activos')}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
              tab === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}>{activeClients.length}</span>
          </button>
          <button
            onClick={() => setTab('inactive')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === 'inactive'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            {t('admin.panel.inactiveTab', 'Inactivos')}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
              tab === 'inactive' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}>{inactiveClients.length}</span>
          </button>
        </nav>

        {/* Derecha: progress + sync */}
        <div className="flex items-center gap-4">
          {syncProgress?.active && syncProgress.total > 0 && (() => {
            const pct = Math.round(((syncProgress.completed + syncProgress.failed) / syncProgress.total) * 100);
            return (
              <div
                className="flex items-center gap-2 text-xs text-gray-600"
                title={t('admin.panel.syncProgressTooltip', 'Sync en curso. Los clientes se actualizan a medida que terminan.')}
              >
                <span className="font-medium">
                  {syncProgress.completed + syncProgress.failed}/{syncProgress.total} ({pct}%)
                </span>
                <div className="w-28 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {syncProgress.failed > 0 && (
                  <span className="text-red-600 font-medium" title={t('admin.panel.syncFailedTooltip', 'Clientes que no pudieron sincronizar.')}>
                    · {syncProgress.failed} {t('admin.panel.syncFailed', 'fallaron')}
                  </span>
                )}
              </div>
            );
          })()}

          {showSyncButton && tab === 'active' && (
            <div className="flex flex-col items-end py-2">
              <span
                className="text-[10px] text-gray-400 leading-none mb-1"
                title={t('admin.panel.lastUpdatedTooltip', 'Última fecha registrada de sincronización de tomas (con hora si está disponible).')}
              >
                {t('admin.panel.lastUpdated', 'Actualizado')}: {formatLastSync(lastSyncAt, lastSyncHasTime)}
              </span>
              <button
                onClick={onSync}
                disabled={syncing || !onSync}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {syncing
                  ? t('admin.panel.syncing', 'Sincronizando...')
                  : syncLabel || t('admin.panel.sync', 'Sincronizar')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-10">
            <Building2 className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              {tab === 'active'
                ? t('admin.panel.noActive', 'No hay clientes en esta vista')
                : t('admin.panel.noInactive', 'No hay clientes inactivos')}
            </p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={visible.map(c => c.uid)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {visible.map((client) => (
                  <SortableClientCard
                    key={client.uid}
                    id={client.uid}
                    canDrag={!!onReorder && tab === 'active'}
                  >
                    <ClientCard
                      client={client}
                      onRefresh={onRefresh}
                      onClientPatch={onClientPatch}
                      onVideoUpload={onVideoUpload}
                      uploadingVideo={uploadingVideo}
                      uploadingGreetingVideo={uploadingGreetingVideo}
                      panelSyncing={syncing}
                      autoOpenEdit={autoOpenEditUid === client.uid}
                    />
                  </SortableClientCard>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default ClientsPanel;
